/**
 * D321 (`selogate-prd.md` §2) — the staged-rollout cohort bucket, R8 §3.1/§3.2
 * (`docs/prds/supplementary/R8-staged-rollout-and-cohorts.md`).
 *
 * **No `contracts.md` section.** This module is not part of the frozen §§1–12 contract layer —
 * D321 is Stage 1 (`selogate-prd.md` §12) rather than a `contracts.md` amendment, and
 * `contracts.md` is frozen. `docs/compliance/implementation-notes.md`'s P0 entry records that as
 * a judgement call rather than a section number invented here.
 *
 * **Two exports, and nothing else (R8 §4 S1's own scope line).**
 *
 *  - `cohortBucket(salt, identity)` — a stable bucket in `[0, 100)` for one `(salt, identity)`
 *    pair. D321: the identity is the signed-in account's Cognito `sub`, hashed **on the
 *    device** — never on the server, and never a device-local id (R8 §3.1). Computing it here,
 *    against the caller-supplied `salt` and `identity` rather than reading either from a stored
 *    record, is what keeps this module ignorant of what a `sub` is or where a rule's `salt`
 *    comes from; both are the config module's business (R8 §2, unbuilt in this tree).
 *  - `resolveFlagArm(rule, bucket)` — which arm (`'on'` | `'off'`) a bucket takes under a rule.
 *    **Malformed and out-of-range resolve to `'off'`, never `'on'`** (R8 §3.2's fail-closed
 *    rule) — a rule that is not an object, whose `salt` is not a non-empty string, or whose
 *    `percent` is not an integer in `[0, 100]`, is malformed and takes `'off'`.
 *
 * **`percent: 0` and `percent: 100` are the ordinary ends of the range, not special cases**
 * (R8 §3.2): `bucket < percent` is false for every `bucket` in `[0, 100)` when `percent` is 0,
 * and true for every `bucket` in that range when `percent` is 100. No branch here names either
 * value.
 *
 * **Returns the arm name, not `rule.on`/`rule.off`'s value.** R8's wire shape carries `on`/`off`
 * payload values beside `percent`/`salt`, but this module takes no dependency on what those
 * values are typed as (D327/D328: a flag's value slot is opaque, per-flag data) — the caller
 * substitutes its own `rule.on`/`rule.off` once it has the arm name. A judgement call the PRD
 * left open; see the session report.
 *
 * **The hash is FNV-1a with a finalizer, not Web Crypto.** `AGENTS.md` §4.6's rule for this package is
 * `globalThis.crypto` as the CSPRNG primitive on both sides — for `generateGateId`'s *random*
 * draw. A cohort bucket is the opposite requirement: **deterministic**, so the same
 * `(salt, identity)` reproduces the same bucket on every call, on both the device and (were it
 * ever needed) a test harness — and `SubtleCrypto.digest()` is asynchronous, which a synchronous
 * `cohortBucket` cannot await into. FNV-1a is a fixed, well-distributed, non-cryptographic
 * string hash; nothing here is a security boundary (D321: the boundary is that the identity
 * never leaves the device, not that the bucket resists prediction), so no CSPRNG is owed. It
 * uses no Node or Web Crypto API at all, which keeps the browser-bundling rule trivially true.
 *
 * **The finalizer is load-bearing and must not be removed as redundant.** Bare FNV-1a carries the
 * salt as a PREFIX, and its low bits are not avalanched: `h = (h ^ byte) * PRIME` computes the low
 * two bits of the product from the low two bits of its inputs alone, so after the identical
 * identity suffix, `bucket % 4` is a function of the salt's trailing state and nothing else. Two
 * salts therefore agree on `bucket % 4` for **every** identity or for **none** — measured over
 * 20,000 UUID identities and 28 salt pairs, `agree mod 4` was 0 or 20,000 and never in between,
 * and same-bucket agreement was 0 or ~832 where ~200 is chance. That is R8 §3.2's *independent
 * draws* falsified: a quarter of salt pairs are perfectly correlated in two bits. `fmix32` —
 * murmur3's finalizer, a fixed public-domain bit-mixer — avalanches those bits; the same
 * measurement then reads 4,861–5,146 and 172–247. `cohort.test.ts` asserts both SIDES of that
 * band, because the broken form's signature is **zero** agreement, which a one-sided upper bound
 * passes more comfortably than the correct form does.
 */
/** Bucket space: `[0, 100)`. R8 §3.2's whole percent range, both ends ordinary. */
export const COHORT_BUCKET_COUNT = 100;
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
/**
 * FNV-1a, 32-bit, over the UTF-8 bytes of `value`. Deterministic and synchronous.
 *
 * Not exported: it is this module's implementation detail, not a second hash primitive for a
 * caller to reach for. `Math.imul` keeps the multiply inside 32 bits without the `>>> 0` on
 * every step that plain `*` would need to avoid float drift.
 */
function fnv1a32(value) {
    let hash = FNV_OFFSET_BASIS;
    for (const byte of new TextEncoder().encode(value)) {
        hash ^= byte;
        hash = Math.imul(hash, FNV_PRIME);
    }
    return hash >>> 0;
}
/**
 * murmur3's `fmix32` — the avalanche step, not a second hash primitive.
 *
 * Not exported, and not optional: see the module docblock. It exists so that a change anywhere in
 * the input reaches every output bit, which bare FNV-1a does not deliver in its low two bits.
 */
function fmix32(hash) {
    let h = hash;
    h ^= h >>> 16;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    return h >>> 0;
}
/**
 * A stable bucket in `[0, 100)` for `(salt, identity)`.
 *
 * **Same inputs, same output, every call** — R8 §4 S1's done-when. `salt` and `identity` are
 * hashed as a `JSON.stringify`d pair rather than a plain concatenation, so `("ab", "c")` and
 * `("a", "bc")` — two different pairs a bare `salt + identity` would collide on — hash
 * differently. A control-character separator between the two was considered and rejected: this
 * package's `scripts/contract-checks.mjs` documents (in its own `globToRegExp` comment) that a
 * single such byte once made that whole file read as binary to git and ripgrep, which is a cost
 * `JSON.stringify` avoids for free. This is this function's own collision to avoid, not a rule
 * R8 states.
 */
export function cohortBucket(salt, identity) {
    const hash = fmix32(fnv1a32(JSON.stringify([salt, identity])));
    return hash % COHORT_BUCKET_COUNT;
}
function isWellFormedRule(rule) {
    if (typeof rule !== 'object' || rule === null)
        return false;
    const candidate = rule;
    if (typeof candidate['salt'] !== 'string' || candidate['salt'].length === 0)
        return false;
    const percent = candidate['percent'];
    return typeof percent === 'number' && Number.isInteger(percent) && percent >= 0 && percent <= 100;
}
function isValidBucket(bucket) {
    return typeof bucket === 'number' && Number.isInteger(bucket) && bucket >= 0 && bucket < COHORT_BUCKET_COUNT;
}
/**
 * Which arm — `'on'` or `'off'` — `bucket` takes under `rule`.
 *
 * **Fail-closed on a malformed or out-of-range input, never `'on'`** (R8 §3.2). `rule` is typed
 * `unknown` rather than `CohortFlagRule` on purpose: a config document is untrusted input at
 * this boundary (D327/D328's payload has not been schema-validated by anything in this tree),
 * and `resolveFlagArm` is the one place that has to be honest about that rather than assuming
 * its caller already was.
 */
export function resolveFlagArm(rule, bucket) {
    if (!isWellFormedRule(rule))
        return 'off';
    if (!isValidBucket(bucket))
        return 'off';
    return bucket < rule.percent ? 'on' : 'off';
}
//# sourceMappingURL=cohort.js.map